import { useMemo } from "react";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import noop from "lodash/fp/noop";

import { RootState as RS } from "../../../../../store/types";

import { useFavouriteStatusToggler } from "../../../../../store/favouriteTemplates/hooks/useActions";
import { OnClickSideEffects } from "../../types";
import { getFavouriteTemplatesById } from "../../../../../store/favouriteTemplates/selectors";
import { TemplateData } from "./FavouriteVariant";
import { createOverviewLink } from "../../../../../utils/internalLinksParsing";
import { getNotificationsByTemplateId } from "../../../../../store/notifications/template/selectors";
import { getNotificationColor } from "../../../cards/TemplateCard/variants/kpi/utils";
import { useTheme } from "../../../../../config/emotion";
import { Colors } from "../../../../../globalTypes/theme";

const passTemplateIds = (state: RS, templateIds: number[]) => templateIds;

type Params = {
  onClickSideEffects?: (id: number) => void;
  toggleFavourite: (id: number, isFavourite: boolean) => void;
  colors: Colors;
};

const makeGetTemplateDataFromIds = ({
  onClickSideEffects = noop,
  toggleFavourite,
  colors,
}: Params) =>
  createSelector(
    getFavouriteTemplatesById,
    getNotificationsByTemplateId,
    passTemplateIds,
    (
      favouriteTemplates,
      notificationsByTemplateId,
      templateIds
    ): TemplateData[] =>
      templateIds
        .map((id) => {
          const {
            order = NaN,
            type,
            image = "",
            kpiMetadata = null,
            name = "",
          } = favouriteTemplates[id] ?? {};

          const notificationCount = notificationsByTemplateId[id]?.length || 0;

          const kpiColor = getNotificationColor(
            kpiMetadata,
            colors,
            notificationCount
          );

          const templateData: TemplateData = {
            // to templateId sem posílám 2x, protože je to přehlednější a menší oser,
            // než vytvářet další intermediate iface.
            // Sortable potřebuje, aby se to jmenovalo id a karta templateId
            id,
            templateId: id,
            icon: image,
            isFavourite: true,
            order,
            favouriteId: id,
            label: name,
            linkTo: createOverviewLink({
              id,
              type,
            }),
            notificationCount,
            backgroundColor: kpiColor,
            isKpi: !!kpiMetadata?.displayAsKpi,
            toggleFavourite: () => toggleFavourite(id, true),
            onClick: () => onClickSideEffects(id),
          };

          return templateData;
        })
        .sort((a, b) => a.order - b.order)
  );

const useTemplateDataFromIds = (
  favouriteIds: number[], // should be memoized, but already is if you get it with reselect
  onClickSideEffects?: OnClickSideEffects // SHOULD BE MEMOIZED!
): TemplateData[] => {
  const toggleFavourite = useFavouriteStatusToggler();
  const { colors } = useTheme();

  // makeGet pattern kvůli tomu curryingu
  // (abych tam dokázal poslat toggleFavourite a onClickSideEffects)
  const getTemplateDataFromIds = useMemo(
    () =>
      makeGetTemplateDataFromIds({
        toggleFavourite,
        onClickSideEffects,
        colors,
      }),
    [toggleFavourite, onClickSideEffects, colors]
  );

  return useSelector((state) => getTemplateDataFromIds(state, favouriteIds));
};

export default useTemplateDataFromIds;
